/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { getStylist } from 'src/core/themes/luisStylist';

export type LegendColor = 'correct' | 'incorrect' | 'unclear' | 'none' | 'lightIncorrect' | 'lightUnclear' | 'other';

export type LegendItemProps = {
    /**
     * The Legend item color corresponding to the legend row.
     */
    color: LegendColor;

    /**
     * Indicator either the item is dimmed or not.
     */
    isDimmed?: boolean;

    /**
     * Callback that is invoked when item is clicked.
     */
    onClick?: () => void;
};

const { styleDiv } = getStylist('Dashboard');

const Item = styleDiv<{ color: LegendColor; isClickable: boolean; isDimmed?: boolean }>(
    'LegendItem',
    {},
    ({ customProps: { color, isClickable, isDimmed } }, theme) => {
        let baseItem = {
            width: theme.legendDimension,
            height: theme.legendDimension,
            marginRight: theme.legendSpacing,
            display: 'inline-block',
            backgroundColor: 'transparent',
            opacity: 1,
            verticalAlign: 'middle',
            cursor: isClickable ? 'pointer' : '',
            border: ''
        };

        switch (color) {
            case 'correct':
                baseItem = {
                    ...baseItem,
                    backgroundColor: isDimmed ? theme.emptyLegendColor : theme.correctColor,
                    border: theme.legendBorder + theme.correctColor
                };
                break;
            case 'incorrect':
                baseItem = {
                    ...baseItem,
                    backgroundColor: isDimmed ? theme.emptyLegendColor : theme.incorrectColor,
                    border: theme.legendBorder + theme.incorrectColor
                };
                break;
            case 'unclear':
                baseItem = {
                    ...baseItem,
                    backgroundColor: isDimmed ? theme.emptyLegendColor : theme.unclearColor,
                    border: theme.legendBorder + theme.unclearColor
                };
                break;
            case 'lightIncorrect':
                baseItem = {
                    ...baseItem,
                    backgroundColor: theme.incorrectColor,
                    opacity: 0.5,
                    border: theme.legendBorder + theme.incorrectColor
                };
                break;
            case 'lightUnclear':
                baseItem = {
                    ...baseItem,
                    backgroundColor: theme.unclearColor,
                    opacity: 0.5,
                    border: theme.legendBorder + theme.unclearColor
                };
                break;
            case 'other':
                baseItem = { ...baseItem, backgroundColor: theme.othersColor, border: theme.legendBorder + theme.othersColor };
                break;
            default:
        }

        return baseItem;
    }
);

/**
 * The colored legend item.
 */
export const LegendItem = (props: LegendItemProps) => {
    const { color, onClick = null, isDimmed = false } = props;

    return (
        <Item
            data-is-focusable="true"
            customProps={{ color, isClickable: onClick !== null, isDimmed }}
            onClick={onClick}
        />
    );
};
